import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import { BonusTypeEnum } from 'src/common/constants';
import { isEmptyObj } from 'src/common/helpers';
import { API_ENDPOINTS } from 'src/setup/client/api-endpoints';
import HttpClient from 'src/setup/client/http-client';
import {
    TotalAssets,
    UserAccountLevelInfo,
    UserBaseInfo,
    UserInviteInfo,
    UserVerifyInfo,
    FavoritePayload,
    UserFeaturesConfig,
    UserKycConfig,
    UserBonus,
} from 'src/types';

interface UserState {
    loading: Record<string, boolean>;
    error: Record<string, any>;
    userinfo: UserBaseInfo;
    userVerifyInfo: UserVerifyInfo;
    userVerifyInfoDirty: boolean;
    userInviteInfo: UserInviteInfo;
    userAccountLevelInfo: UserAccountLevelInfo;
    assets: TotalAssets;
    favorites: string[];
    isLogin: boolean;
    isTokenReady: boolean;
    userFeaturesConfig: UserFeaturesConfig;
    userKycConfig: UserKycConfig;
    userBonus: UserBonus;
}

interface PairsObject {
    [key: string]: boolean;
}

const userPersistConfig = {
    key: 'user',
    storage,
    whitelist: ['userFeaturesConfig', 'userKycConfig', 'userVerifyInfo', 'userVerifyInfoDirty'],
};

const initialState: UserState = {
    loading: {},
    error: {},
    userinfo: JSON.parse(window.sessionStorage.getItem('userinfo') || '{}') as UserBaseInfo,
    userVerifyInfo: {} as UserVerifyInfo,
    userVerifyInfoDirty: false,
    userInviteInfo: {} as UserInviteInfo,
    userAccountLevelInfo: {} as UserAccountLevelInfo,
    assets: {} as TotalAssets,
    isLogin: !isEmptyObj(JSON.parse(window.sessionStorage.getItem('userinfo') || '{}')),
    isTokenReady: false,
    favorites: Object.keys(JSON.parse(window.localStorage.getItem('favorite') || '{}')) as string[],
    userFeaturesConfig: {} as UserFeaturesConfig,
    userKycConfig: {} as UserKycConfig,
    userBonus: {} as UserBonus,
};

// Async thunk actions and status keys
export const AsyncActionKeys = {
    fetchUserLevelInfo: 'fetchUserLevelInfo',
    fetchUserBaseInfo: 'fetchUserBaseInfo',
    createFavorite: 'createFavorite',
    cancelFavorite: 'cancelFavorite',
    logout: 'logout',
    getUserAssets: 'getUserAssets',
    refreshAuthToken: 'refreshAuthToken',
    fetchUserVerifyInfo: 'fetchUserVerifyInfo',
    getUserInviteCode: 'getUserInviteCode',
    openFutures: 'openFutures',
    fetchUserFeaturesConfig: 'fetchUserFeaturesConfig',
    fetchUserKycConfig: 'fetchUserKycConfig',
    fetchUserBonus: 'fetchUserBonus',
};

export const fetchUserLevelInfo = createAsyncThunk(
    AsyncActionKeys.fetchUserLevelInfo,
    async (_, thunkAPI) => {
        try {
            const response = await HttpClient.get(API_ENDPOINTS.GET_USER_LEVEL_INFO);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const fetchUserBaseInfo = createAsyncThunk(
    AsyncActionKeys.fetchUserBaseInfo,
    async (_, thunkAPI) => {
        try {
            const response = await HttpClient.get(API_ENDPOINTS.USER_BASE_INFO);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const createFavorite = createAsyncThunk(
    AsyncActionKeys.createFavorite,
    async ({ body, isLogin }: { body: FavoritePayload; isLogin: Boolean }, thunkAPI) => {
        try {
            if (isLogin) {
                await HttpClient.post(API_ENDPOINTS.CREATE_FAVORITE, body);
            }
            return body;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);
export const cancelFavorite = createAsyncThunk(
    AsyncActionKeys.cancelFavorite,
    async ({ body, isLogin }: { body: FavoritePayload; isLogin: Boolean }, thunkAPI) => {
        try {
            if (isLogin) {
                await HttpClient.post(API_ENDPOINTS.CANCEL_FAVORITE, body);
            }
            return body;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const logout = createAsyncThunk(AsyncActionKeys.logout, async (_, thunkAPI) => {
    try {
        const response = await HttpClient.get(API_ENDPOINTS.LOGOUT);
        return response.data;
    } catch (error) {
        return thunkAPI.rejectWithValue(error);
    }
});

export const getUserAssets = createAsyncThunk(
    AsyncActionKeys.getUserAssets,
    async (body: Object, thunkAPI) => {
        try {
            const response = await HttpClient.post(API_ENDPOINTS.USER_ASSETS_INFO, body);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const refreshAuthToken = createAsyncThunk(
    AsyncActionKeys.refreshAuthToken,
    async (_, thunkAPI) => {
        try {
            const response = await HttpClient.get(API_ENDPOINTS.REFRESH_AUTH_TOKEN);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const fetchUserVerifyInfo = createAsyncThunk(
    AsyncActionKeys.fetchUserVerifyInfo,
    async (_, thunkAPI) => {
        try {
            const response = await HttpClient.get(API_ENDPOINTS.GET_USER_VERIFY_INFO);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const getUserInviteCode = createAsyncThunk(
    AsyncActionKeys.getUserInviteCode,
    async (_, thunkAPI) => {
        try {
            const response = await HttpClient.get(API_ENDPOINTS.GET_USER_INVITE_CODE);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const openFutures = createAsyncThunk(AsyncActionKeys.openFutures, async (_, thunkAPI) => {
    try {
        const response = await HttpClient.get(API_ENDPOINTS.OPEN_FUTURES);
        return response.data;
    } catch (error) {
        return thunkAPI.rejectWithValue(error);
    }
});

export const fetchUserFeaturesConfig = createAsyncThunk(
    AsyncActionKeys.fetchUserFeaturesConfig,
    async (_, thunkAPI) => {
        try {
            const response = await HttpClient.get(API_ENDPOINTS.GET_USER_FEATURES_CONFIG);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const fetchUserKycConfig = createAsyncThunk(
    AsyncActionKeys.fetchUserKycConfig,
    async (_, thunkAPI) => {
        try {
            const response = await HttpClient.get(API_ENDPOINTS.GET_USER_KYC_CONFIG);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const fetchUserBonus = createAsyncThunk(
    AsyncActionKeys.fetchUserBonus,
    async (_, thunkAPI) => {
        try {
            const response = await HttpClient.get(API_ENDPOINTS.GET_USER_BONUS);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

//slice
export const userSlice = createSlice({
    name: 'userSlice',
    initialState,
    reducers: {},
    extraReducers(builder) {
        builder
            .addCase(fetchUserLevelInfo.fulfilled, (state, action) => {
                state.userAccountLevelInfo = action.payload;
            })
            .addCase(fetchUserBaseInfo.fulfilled, (state, action) => {
                const userInfo: UserBaseInfo = action.payload;
                state.userinfo = userInfo;
                state.favorites = userInfo.favorites.map((fav) => fav.symbolId);
                state.isLogin = true;
                window.sessionStorage.userinfo = JSON.stringify(userInfo);
            })
            .addCase(createFavorite.fulfilled, (state, action) => {
                const payload: FavoritePayload = action.payload;
                state.favorites.push(payload.symbol_ids);
                if (!state.isLogin) {
                    const pairs: PairsObject = {};
                    state.favorites.forEach((pair) => {
                        pairs[pair] = true;
                    });
                    window.localStorage.favorite = JSON.stringify(pairs);
                }
            })
            .addCase(cancelFavorite.fulfilled, (state, action) => {
                const payload: FavoritePayload = action.payload;
                state.favorites = state.favorites.filter((fav) => fav !== payload.symbol_ids) || [];
                if (!state.isLogin) {
                    const pairs: PairsObject = {};
                    state.favorites.forEach((pair) => {
                        pairs[pair] = true;
                    });
                    window.localStorage.favorite = JSON.stringify(pairs);
                }
            })
            .addCase(logout.fulfilled, (state) => {
                state.userinfo = {} as UserBaseInfo;
                state.favorites = Object.keys(
                    JSON.parse(window.localStorage.getItem('favorite') || '{}'),
                ) as string[];
                state.isLogin = false;
                state.userVerifyInfoDirty = false;
            })
            .addCase(getUserAssets.fulfilled, (state, action) => {
                state.assets = action.payload;
            })
            .addCase(refreshAuthToken.fulfilled, (state, action) => {
                state.isTokenReady = action.payload.success;
            })
            .addCase(fetchUserVerifyInfo.fulfilled, (state, action) => {
                state.userVerifyInfo = action.payload;
                state.userVerifyInfoDirty = true;
            })
            .addCase(getUserInviteCode.fulfilled, (state, action) => {
                state.userInviteInfo = action.payload;
            })
            .addCase(openFutures.fulfilled, (state, action) => {
                const userInfo = { ...state.userinfo, openFuture: action.payload.success };
                state.userinfo = userInfo;
                window.sessionStorage.userinfo = JSON.stringify(userInfo);
            })
            .addCase(fetchUserFeaturesConfig.fulfilled, (state, action) => {
                state.userFeaturesConfig = action.payload;
            })
            .addCase(fetchUserKycConfig.fulfilled, (state, action) => {
                state.userKycConfig = action.payload;
            })
            .addCase(fetchUserBonus.fulfilled, (state, action) => {
                const payload = action.payload;
                const userBonus =
                    Array.isArray(payload) && payload.length > 0
                        ? payload.find((item) => item.type === BonusTypeEnum.FUTURES_ACCOUNT) ?? {}
                        : {};
                state.userBonus = userBonus;
            });

        // Loading and error handling for async actions
        builder.addMatcher(
            (action) => action.type.endsWith('/pending'),
            (state, action) => {
                const actionType = action.type.split('/')[0];

                state.loading[actionType] = true;
            },
        );
        builder.addMatcher(
            (action) => action.type.endsWith('/fulfilled'),
            (state, action) => {
                const actionType = action.type.split('/')[0];

                state.loading[actionType] = false;
                state.error[actionType] = undefined;
            },
        );
        builder.addMatcher(
            (action) => action.type.endsWith('/rejected'),
            (state, action) => {
                const actionType = action.type.split('/')[0];

                state.loading[actionType] = false;
                state.error[actionType] = action.payload;
            },
        );
    },
});

export const persistedUserReducer = persistReducer<UserState>(userPersistConfig, userSlice.reducer);
